/**
 * Add-on dialog (snippets/omi-addon-dialog.liquid), following the Lifekind flow in OMI style.
 *
 * The add-on checklist under the buy buttons (blocks/omi-addon-checkboxes.liquid) decides whether the
 * dialog opens after the mattress is added to the bag, and which sections start included. The dialog
 * shows the foundation matched to the mattress size and fabric (height choice), upgraded delivery
 * and haul-away, each with an Include toggle. Chosen add-ons go to the bag as their own lines; the
 * cart drawer and bag count refresh through the theme's cart event, then a confirmation offers
 * checkout or more shopping.
 *
 * Opens for adds from the product form and from the layer builder (source "omi-layer-builder").
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const SOURCES = ['product-form-component', 'omi-layer-builder'];

/** @param {string} value */
const norm = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace('california king', 'cal. king')
    .replace('cal king', 'cal. king');

/** @param {string} s */
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);

class OmiAddonDialog extends HTMLElement {
  /** @type {any} */ data = null;
  state = {
    size: '',
    fabric: '',
    height: '',
    delivery: 0,
    haulaway: 0,
    include: { foundation: false, delivery: false, haulaway: false },
  };

  connectedCallback() {
    try {
      this.data = JSON.parse(this.querySelector('script[data-omi-addons]')?.textContent || 'null');
    } catch {
      this.data = null;
    }
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    this.sections = /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-sections]'));
    if (!this.data || !this.dialog) return;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLines);
    this.dialog.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('change', this.#onChange);
    this.dialog.addEventListener('close', () => this.opener?.focus?.());
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLines);
  }

  /** The add-ons ticked in the product page checklist */
  checkedAddons() {
    const list = document.querySelector('[data-omi-addon-list]');
    if (!list) return [];
    return [...list.querySelectorAll('[data-omi-addon-choice]:checked')].map((i) => /** @type {HTMLInputElement} */ (i).value);
  }

  /**
   * Whether this add will be followed by the add-on dialog: an add from the main product form or the
   * layer builder, with add-ons ticked in the checklist. The cart drawer then stays closed.
   * @param {any} event
   */
  claims(event) {
    if (!this.data || event?.action !== 'add') return false;
    const source = event.target instanceof Element ? event.target.closest('product-form-component, omi-layer-builder') : null;
    if (!source || source.closest('dialog')) return false;
    const list = document.querySelector('[data-omi-addon-list]');
    if (!list || source.closest('.shopify-section') !== list.closest('.shopify-section')) return false;
    return this.checkedAddons().length > 0;
  }

  /** @param {any} event */
  #onCartLines = (event) => {
    if (event.action !== 'add' || this.dialog?.open) return;
    Promise.resolve(event.promise)
      .then((result) => {
        const detail = result?.detail;
        if (!detail || detail.didError || !SOURCES.includes(detail.source)) return;
        if (String(detail.productId) !== this.dataset.productId) return;
        const selected = this.checkedAddons();
        if (!selected.length) return;
        const line = (detail.items || []).find((/** @type {any} */ item) => String(item.product_id) === this.dataset.productId);
        this.open(line, selected);
      })
      .catch(() => {});
  };

  /** @param {any} line @param {string[]} selected */
  open(line, selected) {
    const sizeOption = (line?.options_with_values || []).find((/** @type {any} */ o) => /size/i.test(o.name));
    this.state.size = norm(sizeOption?.value || line?.variant_title || '');
    this.state.fabric = this.dataset.fabric || '';
    this.state.height = '';
    this.state.include = {
      foundation: selected.includes('foundation'),
      delivery: selected.includes('delivery'),
      haulaway: selected.includes('haulaway'),
    };
    const first = (/** @type {any} */ p) => (p?.variants || []).find((/** @type {any} */ v) => v.available)?.id || 0;
    this.state.delivery = first(this.data.delivery);
    this.state.haulaway = first(this.data.haulaway);

    /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-choose]')).hidden = false;
    /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-done]')).hidden = true;
    /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-error]')).hidden = true;
    const add = /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-addons-add]'));
    add.disabled = false;
    add.textContent = 'Add selected to bag';

    this.render();
    this.opener = /** @type {HTMLElement | null} */ (document.activeElement);
    this.#announce('Add-on options dialog opened.');
    // The cart drawer stays closed for this add (see claims())
    requestAnimationFrame(() => this.dialog?.showModal());
  }

  /** @param {number} cents */
  money(cents) {
    if (!cents) return 'Free';
    const amount = cents / 100;
    return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 });
  }

  /** @param {any} v */
  priceHtml(v) {
    if (!v) return '';
    // Trade customers: pill + MSRP filled in by omi-trade-pricing.js (only defined for B2B customers)
    const trade = customElements.get('omi-trade-price')
      ? (part) =>
          ` <omi-trade-price class="omi-trade omi-trade--${part} is-pending" data-part="${part}" data-variant="${v.id}" data-trade="${v.price}">${part === 'tag' ? '<span class="omi-trade__tag">Trade price</span>' : '&nbsp;'}</omi-trade-price>`
      : () => '';
    if (v.compare_at_price > v.price) {
      return `${trade('tag')}<span class="price price-item--sale">${this.money(v.price)}</span> <s class="compare-at-price">${this.money(v.compare_at_price)}</s>`;
    }
    return `${trade('tag')}<span class="price">${this.money(v.price)}</span>${trade('msrp')}`;
  }

  /** @param {any} product @param {string} name */
  optionIndex(product, name) {
    return (product?.options || []).findIndex((/** @type {any} */ o) => new RegExp(name, 'i').test(typeof o === 'string' ? o : o.name));
  }

  foundationVariants() {
    const p = this.data.foundation;
    const si = this.optionIndex(p, 'size');
    return (p?.variants || []).filter((/** @type {any} */ v) => v.available && (si < 0 || norm(v.options[si]) === this.state.size));
  }

  foundationVariant() {
    const p = this.data.foundation;
    const list = this.foundationVariants();
    const fi = this.optionIndex(p, 'fabric');
    const hi = this.optionIndex(p, 'height');
    return (
      list.find((/** @type {any} */ v) => (fi < 0 || norm(v.options[fi]) === norm(this.state.fabric)) && (hi < 0 || v.options[hi] === this.state.height)) ||
      list.find((/** @type {any} */ v) => fi < 0 || norm(v.options[fi]) === norm(this.state.fabric)) ||
      list[0] ||
      null
    );
  }

  sizeLabel() {
    return this.state.size.replace(/\b\w/g, (c) => c.toUpperCase()).replace('Xl', 'XL');
  }

  /** @param {'foundation' | 'delivery' | 'haulaway'} key @param {string} title @param {string} aside */
  head(key, title, aside = '') {
    const on = this.state.include[key];
    return `<div class="omi-addons__section-head">
      <label class="omi-addons__include">
        <input type="checkbox" data-omi-include="${key}"${on ? ' checked' : ''} aria-label="Include ${esc(title)}">
        <span class="omi-addons__include-box" aria-hidden="true"></span>
        <span class="omi-addons__include-label">Include</span>
      </label>
      <p class="omi-addons__section-title">${esc(title)}</p>
      ${aside ? `<span class="omi-addons__section-aside">${esc(aside)}</span>` : ''}
    </div>`;
  }

  renderFoundation() {
    const p = this.data.foundation;
    const list = this.foundationVariants();
    if (!p || !list.length) return '';
    const fi = this.optionIndex(p, 'fabric');
    const hi = this.optionIndex(p, 'height');
    const fabrics = fi < 0 ? [] : [...new Set(list.map((/** @type {any} */ v) => v.options[fi]))];
    const heights = hi < 0 ? [] : [...new Set(list.map((/** @type {any} */ v) => v.options[hi]))];
    if (fi >= 0 && !fabrics.some((f) => norm(f) === norm(this.state.fabric))) this.state.fabric = fabrics[0] || '';
    if (hi >= 0 && !heights.includes(this.state.height)) this.state.height = heights[heights.length - 1] || '';
    const v = this.foundationVariant();
    const skipped = this.state.include.foundation ? '' : ' is-skipped';
    return `<section class="omi-addons__section${skipped}" data-section="foundation">
      ${this.head('foundation', p.title, 'Size & fabric matched')}
      <div class="omi-addons__section-body omi-addons__foundation">
        ${(v?.image || this.data.foundationImage) ? `<img class="omi-addons__foundation-img" src="${esc(v?.image || this.data.foundationImage)}" alt="" width="120" height="120" loading="lazy">` : ''}
        <div class="omi-addons__foundation-detail">
          ${
            heights.length > 1
              ? `<div class="omi-addons__choice">
                   <p class="omi-addons__label">Height</p>
                   <div class="omi-addons__segments" role="group" aria-label="Foundation height">
                     ${heights.map((h) => `<button type="button" data-omi-height="${esc(h)}" aria-pressed="${h === this.state.height}">${esc(h)}</button>`).join('')}
                   </div>
                 </div>`
              : ''
          }
          ${
            fabrics.length > 1
              ? `<div class="omi-addons__choice">
                   <p class="omi-addons__label">Fabric</p>
                   <div class="omi-addons__segments" role="group" aria-label="Foundation fabric">
                     ${fabrics.map((f) => `<button type="button" data-omi-fabric="${esc(f)}" aria-pressed="${norm(f) === norm(this.state.fabric)}">${esc(f)}</button>`).join('')}
                   </div>
                 </div>`
              : ''
          }
          <div class="omi-addons__price-row">
            <div class="omi-addons__price">${this.priceHtml(v)}</div>
            <span class="omi-addons__price-note">${this.#check()}${esc(this.sizeLabel())}, matched to your mattress. King sizes are split.</span>
          </div>
        </div>
      </div>
    </section>`;
  }

  /** @param {'delivery' | 'haulaway'} key @param {string} title @param {Record<string,string>} notes */
  renderOptions(key, title, notes) {
    const p = this.data[key];
    const variants = (p?.variants || []).filter((/** @type {any} */ v) => v.available);
    if (!variants.length) return '';
    const skipped = this.state.include[key] ? '' : ' is-skipped';
    return `<section class="omi-addons__section${skipped}" data-section="${key}">
      ${this.head(key, title)}
      <div class="omi-addons__section-body" role="radiogroup" aria-label="${esc(title)}">
        ${variants
          .map((/** @type {any} */ v) => {
            const on = this.state[key] === v.id;
            return `<label class="omi-addons__option${on ? ' is-selected' : ''}">
              <input type="radio" name="omi-${key}" value="${v.id}"${on ? ' checked' : ''} data-omi-choice="${key}">
              <span class="omi-addons__radio" aria-hidden="true"></span>
              <span class="omi-addons__option-text">
                <span class="omi-addons__option-name">${esc(v.title)} · ${this.priceHtml(v)}</span>
                ${notes?.[v.title] ? `<span class="omi-addons__option-desc">${esc(notes[v.title])}</span>` : ''}
              </span>
            </label>`;
          })
          .join('')}
      </div>
    </section>`;
  }

  render() {
    const html =
      this.renderFoundation() +
      this.renderOptions('delivery', this.data.delivery?.title || 'Upgraded Delivery', this.data.deliveryNotes) +
      this.renderOptions('haulaway', 'Old Mattress Haul-Away', this.data.haulawayNotes);
    this.sections.innerHTML = html || '<p class="omi-addons__sub">No add-on options are available right now.</p>';
  }

  #check() {
    return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-omi-addons-live]');
    if (!live) return;
    live.textContent = '';
    setTimeout(() => (live.textContent = message), 50);
  }

  selectedItems() {
    /** @type {{id: number, quantity: number}[]} */
    const items = [];
    if (this.state.include.foundation) {
      const v = this.foundationVariant();
      if (v) items.push({ id: v.id, quantity: 1 });
    }
    if (this.state.include.delivery && this.state.delivery) items.push({ id: this.state.delivery, quantity: 1 });
    if (this.state.include.haulaway && this.state.haulaway) items.push({ id: this.state.haulaway, quantity: 1 });
    return items;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const t = /** @type {HTMLInputElement} */ (event.target);
    if (t.dataset.omiInclude) {
      this.state.include[/** @type {'foundation' | 'delivery' | 'haulaway'} */ (t.dataset.omiInclude)] = t.checked;
      t.closest('.omi-addons__section')?.classList.toggle('is-skipped', !t.checked);
    } else if (t.dataset.omiChoice) {
      this.state[/** @type {'delivery' | 'haulaway'} */ (t.dataset.omiChoice)] = Number(t.value);
      const group = t.closest('.omi-addons__section-body');
      for (const option of group?.querySelectorAll('.omi-addons__option') || []) {
        option.classList.toggle('is-selected', option.contains(t));
      }
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    if (t === this.dialog || t.closest('[data-omi-addons-close]')) return this.dialog?.close();
    const height = /** @type {HTMLElement | null} */ (t.closest('[data-omi-height]'));
    const fabric = /** @type {HTMLElement | null} */ (t.closest('[data-omi-fabric]'));
    if (height || fabric) {
      if (height) this.state.height = height.dataset.omiHeight || '';
      if (fabric) this.state.fabric = fabric.dataset.omiFabric || '';
      this.state.include.foundation = true;
      this.render();
      /** @type {HTMLElement | null} */ (this.querySelector(height ? `[data-omi-height="${CSS.escape(this.state.height)}"]` : `[data-omi-fabric="${CSS.escape(this.state.fabric)}"]`))?.focus();
      return;
    }
    if (t.closest('[data-omi-addons-add]')) this.#add();
  };

  async #add() {
    const items = this.selectedItems();
    const root = window.Shopify?.routes?.root || '/';
    if (!items.length) {
      window.location.href = `${root}cart`;
      return;
    }
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-addons-add]'));
    const error = /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-error]'));
    button.disabled = true;
    button.textContent = 'Adding…';
    error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const json = await response.json();
      if (!response.ok || json.status) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: json.sections, source: 'omi-addon-dialog', didError: false },
      });
      /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-choose]')).hidden = true;
      const done = /** @type {HTMLElement} */ (this.querySelector('[data-omi-addons-done]'));
      done.hidden = false;
      this.#announce('Add-ons added to your bag. Choose to check out or keep shopping.');
      /** @type {HTMLElement | null} */ (done.querySelector('a, button'))?.focus();
    } catch (e) {
      deferred.reject(e);
      error.textContent = /** @type {Error} */ (e).message;
      error.hidden = false;
      button.disabled = false;
      button.textContent = 'Add selected to bag';
    }
  }
}

if (!customElements.get('omi-addon-dialog')) customElements.define('omi-addon-dialog', OmiAddonDialog);
